import React from "react";
import { motion } from "framer-motion";

const SCALE_STATS = [
  {
    value: "R2.45T",
    description: "Total value of debit orders processed across South Africa in 2024 alone.",
    source: "BankservAfrica · 2024"
  },
  {
    value: "16.7M",
    description: "Debit order collections processed every single month in South Africa.",
    source: "BankservAfrica · 2024"
  },
  {
    value: "13.8%",
    description: "Dispute rate in 2024 — up from 2.3% a decade ago. The system is under strain.",
    source: "PASA Dispute Data · 2024"
  }
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.10,
      duration: 0.60,
      ease: [0.22, 1, 0.36, 1]
    }
  })
};

export default function StatsSection1() {
  return (
    <>
      <style>{`
        .stats-section-1 {
          background: 
            radial-gradient(70% 60% at 18% 20%, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0) 62%),
            radial-gradient(60% 55% at 72% 30%, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0) 60%),
            linear-gradient(180deg, #1A1060 0%, #130A48 100%);
          padding: 44px 36px 40px;
          border-radius: 16px;
          width: 100%;
          max-width: 1200px;
          margin: 0 auto;
        }

        .stats-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
          padding-bottom: 20px;
          border-bottom: 1px solid rgba(255,255,255,0.07);
          margin-bottom: 28px;
        }

        .stats-header-left {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .stats-badge {
          font-family: 'Fira Code', monospace;
          font-weight: 600;
          font-size: 10px;
          color: rgba(184,146,42,0.70);
          text-transform: uppercase;
          letter-spacing: 0.12em;
        }

        .stats-title {
          font-size: 20px;
          font-weight: 600;
          color: rgba(255,255,255,0.90);
          letter-spacing: -0.02em;
          margin: 0;
        }

        .stats-counter {
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 999px;
          padding: 5px 12px;
          font-family: 'Fira Code', monospace;
          font-size: 11px;
          color: rgba(255,255,255,0.18);
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 14px;
        }

        .stats-card {
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.09);
          border-radius: 14px;
          padding: 26px 22px 20px;
          transition: all 0.25s ease;
        }

        .stats-card:hover {
          background: rgba(255,255,255,0.085);
          border-color: rgba(184,146,42,0.30);
          transform: translateY(-2px);
        }

        .stats-accent {
          width: 28px;
          height: 2px;
          background: rgba(184,146,42,0.45);
          border-radius: 2px;
          margin-bottom: 18px;
        }

        .stats-value {
          font-size: clamp(32px, 4vw, 46px);
          font-weight: 800;
          letter-spacing: -0.04em;
          margin-bottom: 12px;
          background: linear-gradient(135deg, #B8922A 0%, #D4B05E 52%, #E8C878 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .stats-description {
          font-family: 'Outfit', sans-serif;
          font-size: 13px;
          font-weight: 400;
          color: rgba(255,255,255,0.76);
          line-height: 1.62;
          margin-bottom: 16px;
        }

        .stats-divider {
          height: 1px;
          background: rgba(255,255,255,0.07);
          margin-bottom: 10px;
        }

        .stats-source {
          font-family: 'Fira Code', monospace;
          font-size: 10px;
          font-weight: 400;
          color: rgba(255,255,255,0.24);
          text-transform: uppercase;
          letter-spacing: 0.08em;
        }

        @media (max-width: 559px) {
          .stats-section-1 {
            padding: 32px 20px 28px;
          }

          .stats-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 12px;
          }

          .stats-grid {
            grid-template-columns: 1fr;
          }

          .stats-title {
            font-size: 18px;
          }
        }
      `}</style>

      <section className="stats-section-1">
        {/* Section Header */}
        <div className="stats-header">
          <div className="stats-header-left">
            <div className="stats-badge">01 · MARKET SCALE</div>
            <h2 className="stats-title">The size of the system you operate in.</h2>
          </div>
          <div className="stats-counter">01 / 02</div>
        </div>

        {/* Stats Grid */}
        <motion.div 
          className="stats-grid"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-60px" }}
        >
          {SCALE_STATS.map((stat, index) => (
            <motion.div
              key={index}
              className="stats-card"
              variants={fadeUp}
              custom={index}
            >
              <div className="stats-accent"></div>
              <div className="stats-value">{stat.value}</div>
              <p className="stats-description">{stat.description}</p>
              <div className="stats-divider"></div>
              <div className="stats-source">{stat.source}</div>
            </motion.div>
          ))}
        </motion.div>
      </section>
    </>
  );
}
